import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { NAV_CONST } from '@/src/constants/nav';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import dayjs from 'dayjs';
import ImageUploadInfo from '@/src/components/molecule/ImageUploadInfo';
import ImageUpload from '@/src/components/atom/ImageUpload';
import IconButton from '@/src/components/atom/Button/IconButton';
import { useRef } from 'react';
import { APIError } from '@/src/models/common/apiError';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
  FormValues,
} from '@/src/pages/Challenge/field';
import { UpsertChallengeRequestBody } from '@/src/apis/rest/challenge.dtos';
import { getCreditTemplateAPI } from '@/src/apis/rest/creditTemplate';
import { postChallengeAPI } from '@/src/apis/rest/challenge';
import Select from '@/components/atom/Select';
import { UNIT_STRINGS } from '@/src/constants/unit';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoFormSectionCard from '@/components/organism/InfoFormSectionCard/InfoFormSectionCard';

const BACK_PATH = PATH.CHALLENGE_LIST;

export default function ChallengeCreatePage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();

  // form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // query
  const { data: creditTemplateData } = useQuery({
    queryKey: ['credit-template'],
    queryFn: () => {
      return getCreditTemplateAPI({
        page: 1,
        pageSize: 9999,
        category: '챌린지',
        isActive: true,
      });
    },
  });

  // state
  const {
    Dialog: PriorityErrorDialog,
    openDialog: openPriorityErrorDialog,
    isOpen: isPriorityErrorOpen,
    closeDialog: closePriorityErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const createMutation = useMutation({ mutationFn: postChallengeAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  const isFormValid =
    form.formState.isValid &&
    !!form.watch('startedAt') &&
    !!form.watch('endedAt');

  const handleSubmit = (values: UpsertChallengeRequestBody) => {
    createMutation.mutate(
      { ...values, creditTemplateId: Number(values.creditTemplateId) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH);
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'CANNOT_PRIORITY_EXPOSE_OVER_THAN_FIVE') {
            openPriorityErrorDialog();
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!isFormValid}
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={createMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // usePrompt
  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.challengeManagement,
          NAV_CONST.challengeCreate,
        ]}
        pageTitle={NAV_CONST.challengeCreate}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="챌린지 기본 정보" className="col-span-2">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name={formFields.startedDateRange.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent isRequired label="참여 기간" error={error}>
                        <DateRangePicker
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            form.setValue(
                              'startedAt',
                              e?.from
                                ? dayjs(e?.from).format('YYYY-MM-DD')
                                : '',
                            );
                            form.setValue(
                              'endedAt',
                              e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                            );
                          }}
                          dateDisabledMatcher={getDateDisabledMatcher}
                        />
                      </FormContent>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name={formFields.isExposed.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="노출 여부"
                        isRequired={true}
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.exposed, value: 'true' },
                            { label: OPTIONS_CONST.notExposed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.priorityDisplayYn.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="우선 노출 여부"
                        isRequired={true}
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name={formFields.thumbnailImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired
                        label="썸네일 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('thumbnailImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'thumbnailImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name={formFields.creditTemplateId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        className="w-full gap-2 [&>div:last-child]:w-full"
                        label="지급 크레딧"
                        isRequired
                        error={error}
                      >
                        <Select
                          option={[
                            {
                              optionList: creditTemplateData?.rows?.map(
                                template => ({
                                  label: `[챌린지] ${template.title} (${template.credit}${UNIT_STRINGS.CREDIT})`,
                                  value: template.id.toString(),
                                }),
                              ) || [
                                {
                                  label: '등록된 크레딧 카테고리가 없습니다.',
                                  value: '',
                                },
                              ],
                            },
                          ]}
                          rootProps={{
                            value: field.value?.toString(),
                            onValueChange: value => {
                              form.setValue('creditTemplateId', Number(value));
                              field.onChange(Number(value));
                            },
                          }}
                          valueProps={{
                            placeholder: '크레딧 카테고리 선택',
                          }}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.approveExampleImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired={true}
                        label="승인 예시 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('approveExampleImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'approveExampleImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.rejectExampleImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired={true}
                        label="거절 예시 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('rejectExampleImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'rejectExampleImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </div>
          </SectionCard>
          <SectionCard cardTitle="챌린지 내용" className="col-span-2">
            <div className="flex flex-1 flex-col gap-6">
              <FormField
                control={form.control}
                name={formFields.title.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent isRequired label="제목" error={error}>
                      <Input
                        onClear={() => field.onChange(undefined)}
                        placeholder="제목을 입력하세요"
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={formFields.content.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="col-span-3"
                      label="내용"
                      isRequired
                      error={error}
                    >
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <TextArea
                            className="h-[500px]"
                            placeholder="챌린지 내용을 입력하세요"
                            {...field}
                          />
                        </div>
                      </div>
                    </FormContent>
                  );
                }}
              />
            </div>
          </SectionCard>
          <InfoFormSectionCard
            cardTitle="안내사항 영역"
            form={form}
            field={formFields.generateChallengeInfos.name}
            removeField="removeInfoIds"
          />
          <InfoFormSectionCard
            cardTitle="유의사항 영역"
            form={form}
            field={formFields.generateChallengeCautions.name}
            removeField="removeCautionIds"
            buttonText="유의사항 추가하기"
          />

          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* Modal */}
      <PriorityErrorDialog
        open={isPriorityErrorOpen}
        onOpenChange={closePriorityErrorDialog}
        onOk={closePriorityErrorDialog}
        title="우선 노출 여부 지정 불가"
        cancelButtonProps={{
          className: 'hidden',
        }}
      >
        이미 해당 챌린지 기간 내 우선노출 지정 개수가 5개 있습니다.
        <br />
        다른 챌린지를 확인 후 재시도 해주세요.
      </PriorityErrorDialog>
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={createMutation.isPending}
        />
      )}
    </>
  );
}
